import type { ReactNode } from "react";
import React, { useCallback, useContext, useEffect, useState } from "react";

import type { MBMessageTypes } from "../constants";
import { defaultHandlers } from "../default-handlers";
import { useReceiveMessage } from "../use-receive-message";
import { useSendMessage } from "../use-send-message";

type MessageHandler = (payload: any) => void;
type HandlerRegistry = Record<MBMessageTypes, MessageHandler>;
type RegisterHandler = ({
  type,
  listener,
}: {
  type: MBMessageTypes;
  listener: MessageHandler;
}) => void;

type MessengerContext = {
  sendMessage: (message: any) => void;
  registerHandler: RegisterHandler;
};

const CubeMessengerContext = React.createContext<MessengerContext | null>(null);

export const CubeMessengerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const sendMessage = useSendMessage();
  const [handlerRegistry, setHandlerRegistry] = useState<HandlerRegistry>(
    {} as HandlerRegistry,
  );

  /**
   * Listen for messages from the parent container using the
   * useReceiveMessage hook. When a message is received, check
   * if there is a handler registered for the message type and
   * call it if there is.
   */
  useReceiveMessage((message: any) => {
    const handler = handlerRegistry[message.type as MBMessageTypes];
    if (handler) {
      handler(message.payload);
    }
  });

  /**
   * Register a listener for a given message type
   */
  const registerHandler: RegisterHandler = useCallback(
    ({ type, listener }) => {
      if (handlerRegistry[type as MBMessageTypes]) {
        console.error(`Listener already registered for ${type}`);
        return;
      }
      // Register listener
      setHandlerRegistry(registry => ({
        ...registry,
        [type]: listener,
      }));
    },
    [handlerRegistry],
  );

  const messengerContext = {
    sendMessage,
    registerHandler,
  };

  return (
    <CubeMessengerContext.Provider value={messengerContext}>
      {children}
    </CubeMessengerContext.Provider>
  );
};

const useCubeMessengerContext = () => {
  const context = useContext(CubeMessengerContext);
  if (!context) {
    throw new Error(
      "useCubeMessengerContext must be used within a CubeMessengerProvider",
    );
  }
  return context;
};

type UseCubeMessengerProps = {
  // allow for handling a single message type or multiple
  handlers?: Partial<Record<MBMessageTypes, MessageHandler>>;
};

/**
 * A general manager for post messages between Cube and Metabase
 */
export const useCubeMessenger = ({ handlers }: UseCubeMessengerProps = {}) => {
  const { sendMessage, registerHandler } = useCubeMessengerContext();

  useEffect(() => {
    if (!handlers) {
      return;
    }
    // Register default handlers
    Object.entries(defaultHandlers).forEach(([type, listener]) => {
      registerHandler({ type: type as MBMessageTypes, listener });
    });

    // Register handlers
    Object.entries(handlers).forEach(([type, listener]) => {
      registerHandler({ type: type as MBMessageTypes, listener });
    });
    // Ensure the handlers only get registered on first mount:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { sendMessage };
};

/**
 * A HOC for providing the sendMessage function to a component
 * and optionally registering message handlers
 */
export const withCubeMessenger =
  (handlers?: Partial<Record<MBMessageTypes, MessageHandler>>) =>
  <P extends object>(Component: React.ComponentType<P>) => {
    const WithCubeMessenger: ReactNode = (props: P) => {
      const { sendMessage } = useCubeMessenger({ handlers });
      return <Component sendMessage={sendMessage} {...props} />;
    };
    return WithCubeMessenger;
  };
